import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { SupportAgentOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportAccessApi } from '@/api/kyc.api';
import Can from '@/components/common/Can';
import DataState from '@/components/common/DataState';
import { useNotify } from '@/components/common/useNotify';
import { formatDateTime } from '@/utils/format';

const KEY = ['support-access'] as const;

/**
 * Support access (Module 0, O13): TravelERP support can sign in to this workspace only while the company allows
 * it — for 24 or 72 hours, withdrawn at any time. Each support sign-in is listed here.
 */
export function SupportAccessSection() {
  const qc = useQueryClient();
  const notify = useNotify();
  const query = useQuery({ queryKey: KEY, queryFn: supportAccessApi.get });
  const set = useMutation({ mutationFn: supportAccessApi.set, onSuccess: (d) => qc.setQueryData(KEY, d) });
  const a = query.data;

  const change = async (hours: 0 | 24 | 72) => {
    try {
      await set.mutateAsync(hours);
      notify.success(hours ? `Support access allowed for ${hours} hours.` : 'Support access withdrawn.');
    } catch (e) { notify.error(e); }
  };

  return (
    <DataState loading={query.isLoading} error={query.error} onRetry={query.refetch} loadingText="Loading support access…">
      {a && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <SupportAgentOutlined color="primary" />
              <Typography variant="h6" component="h2" fontWeight={700}>Support access</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Let TravelERP support sign in to your workspace to help you. They can sign in only while you allow it,
              and every sign-in is recorded.
            </Typography>

            {a.active ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Allowed until <strong>{formatDateTime(a.until)}</strong>{a.grantedBy ? ` (by ${a.grantedBy})` : ''}.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>Support cannot sign in to your workspace.</Alert>
            )}

            <Can perm="Company.Edit">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant={a.active ? 'outlined' : 'contained'} onClick={() => change(24)} disabled={set.isPending}>Allow for 24 hours</Button>
                <Button variant="outlined" onClick={() => change(72)} disabled={set.isPending}>Allow for 72 hours</Button>
                {a.active && (
                  <Button color="error" onClick={() => change(0)} disabled={set.isPending}>Withdraw now</Button>
                )}
              </Stack>
            </Can>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>Recent support sign-ins</Typography>
              {a.recentSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">None yet.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {a.recentSessions.map((s, i) => (
                    <Typography key={i} variant="body2">{formatDateTime(s.usedAt)}{s.by ? ` · ${s.by}` : ''}</Typography>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </DataState>
  );
}
